/**
 * Add Candidate — resume-first intake with dedup.  ⭐ the most important screen.
 *
 * POST /candidates runs dedup and returns one of three outcomes — we branch on
 * `data.outcome`, never on the HTTP status:
 *   • created   → new person + application.
 *   • linked    → existing person; a new application was added (no duplicate);
 *                 show their prior application history.
 *   • ambiguous → phone matches one person, email another; show BOTH and let the
 *                 user pick → POST /candidates/:id/applications. Never auto-pick.
 *
 * Company rules honored: R1 (never duplicate), R2 (never auto-merge), R4 (source
 * mandatory). A resume selected up-front is uploaded after the candidate exists.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, UserCheck, Users, ArrowRight } from 'lucide-react';
import { Button, FloatingInput, FloatingDropdown, Snackbar } from '../../components/ui';
import { Card } from '../../components/ui/Card';
import {
	ResumeDropzone,
	ResumeUploadCard,
	CandidateSummaryCard,
	StageBadge,
	SourcePill,
	InlineError,
} from '../../features/recruitment';
import { useIntake, useLinkApplication } from '../../features/recruitment/hooks/useCandidates';
import { useJobOptions } from '../../features/recruitment/hooks/useOptions';
import { SOURCE_OPTIONS } from '../../features/recruitment/config/constants';
import { getRecruitmentError, isDuplicateApplication } from '../../features/recruitment/utils/apiError';
import { formatDate } from '../../features/recruitment/utils/format';
import type {
	Candidate,
	IntakeBody,
	IntakeResult,
	IntakeLinked,
	IntakeAmbiguous,
	ApplicationRow,
} from '../../services/recruitmentApi';

export const AddCandidate: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const preselectedJob = searchParams.get('job') ?? '';

	const { data: jobOptions, isLoading: jobsLoading } = useJobOptions();
	const intake = useIntake();
	const linkApplication = useLinkApplication();

	// ── form state ────────────────────────────────────────────────────────────
	const [file, setFile] = useState<File | null>(null);
	const [fullName, setFullName] = useState('');
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [jobId, setJobId] = useState(preselectedJob);
	const [source, setSource] = useState('');
	const [location, setLocation] = useState('');
	const [experience, setExperience] = useState('');
	const [currentCompany, setCurrentCompany] = useState('');
	const [currentRole, setCurrentRole] = useState('');
	const [noticePeriod, setNoticePeriod] = useState('');

	const [touched, setTouched] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	// ── result state ──────────────────────────────────────────────────────────
	const [result, setResult] = useState<IntakeResult | null>(null);
	/** Candidate we ended up on (created, linked, or picked from ambiguous). */
	const [resolvedCandidateId, setResolvedCandidateId] = useState<number | null>(null);
	const [ambiguousError, setAmbiguousError] = useState<string | null>(null);
	const [pickingId, setPickingId] = useState<number | null>(null);

	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({
		open: false,
		message: '',
		type: 'success',
	});

	const jobLabel = useMemo(
		() => jobOptions?.find(o => o.value === jobId)?.label ?? 'this opening',
		[jobOptions, jobId]
	);

	// ── validation ────────────────────────────────────────────────────────────
	// Dedup only matches on a valid 10-digit phone / well-formed email, so we
	// validate the format of whatever contact is provided — a malformed contact
	// would create a person who never dedups (rule R1: never a duplicate person).
	const nameValid = fullName.trim().length > 0;
	const phoneProvided = phone.trim().length > 0;
	const emailProvided = email.trim().length > 0;
	const phoneFormatOk = /^(?:91)?[6-9]\d{9}$/.test(phone.replace(/\D/g, '')); // 10-digit Indian mobile, optional 91 prefix
	const emailFormatOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	const phoneError = phoneProvided && !phoneFormatOk; // provided but malformed
	const emailError = emailProvided && !emailFormatOk;
	const contactMissing = !phoneProvided && !emailProvided; // phone OR email required for dedup
	const contactValid = !contactMissing && !phoneError && !emailError;
	const jobValid = !!jobId;
	const sourceValid = !!source; // rule R4
	const canSubmit = nameValid && contactValid && jobValid && sourceValid && !intake.isPending;

	const buildBody = (): IntakeBody => ({
		full_name: fullName.trim(),
		phone: phone.trim() || undefined,
		email: email.trim() || undefined,
		job_opening_id: Number(jobId),
		source,
		location: location.trim() || undefined,
		total_experience: experience ? Number(experience) : undefined,
		current_company: currentCompany.trim() || undefined,
		current_role: currentRole.trim() || undefined,
		notice_period_days: noticePeriod ? Number(noticePeriod) : undefined,
	});

	const handleSubmit = async () => {
		setTouched(true);
		if (!nameValid) return setFormError('Full name is required.');
		if (contactMissing) return setFormError('Enter a phone number or email — one is required for the duplicate check.');
		if (phoneError) return setFormError('Enter a valid 10-digit mobile number — dedup needs a well-formed phone.');
		if (emailError) return setFormError('Enter a valid email address.');
		if (!jobValid) return setFormError('Select a job opening.');
		if (!sourceValid) return setFormError('Source is required on every application.');
		setFormError(null);

		try {
			const data = await intake.mutateAsync(buildBody());
			setResult(data);
			if (data.outcome === 'created' || data.outcome === 'linked') {
				setResolvedCandidateId(data.candidate.id);
			}
		} catch (e) {
			const re = getRecruitmentError(e);
			setFormError(re.message);
			setSnackbar({ open: true, message: re.message, type: 'error' });
		}
	};

	// Ambiguous → user picks a person → add the application to them.
	const handlePick = async (candidate: Candidate) => {
		setAmbiguousError(null);
		setPickingId(candidate.id);
		try {
			const linked = await linkApplication.mutateAsync({
				candidateId: candidate.id,
				body: { job_opening_id: Number(jobId), source },
			});
			// Treat like a linked outcome so the success + resume upload flow continues.
			setResolvedCandidateId(candidate.id);
			setResult({ outcome: 'linked', matched: true, candidate, application: linked.application, priorApplications: [] });
			setSnackbar({ open: true, message: `Application added to ${candidate.full_name}.`, type: 'success' });
		} catch (e) {
			if (isDuplicateApplication(e)) {
				setAmbiguousError(`${candidate.full_name} has already applied to ${jobLabel}.`);
			} else {
				setAmbiguousError(getRecruitmentError(e).message);
			}
		} finally {
			setPickingId(null);
		}
	};

	const resetToForm = () => {
		setResult(null);
		setResolvedCandidateId(null);
		setAmbiguousError(null);
	};

	const startAnother = () => {
		resetToForm();
		setFile(null);
		setFullName('');
		setPhone('');
		setEmail('');
		setSource('');
		setLocation('');
		setExperience('');
		setCurrentCompany('');
		setCurrentRole('');
		setNoticePeriod('');
		setTouched(false);
		setFormError(null);
	};

	// ────────────────────────────────────────────────────────────────────────────
	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-3xl'>
				<button
					type='button'
					onClick={() => navigate(-1)}
					className='mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800'
				>
					<ArrowLeft className='h-4 w-4' /> Back
				</button>

				<h1 className='text-2xl font-semibold text-gray-900'>Add Candidate</h1>
				<p className='mt-1 text-sm text-gray-500'>We check for an existing person before anything is created.</p>

				{/* Result view replaces the form once we have an outcome. */}
				{result ? (
					<div className='mt-6'>
						{result.outcome === 'created' && (
							<CreatedPanel
								candidate={result.candidate}
								jobLabel={jobLabel}
								file={file}
								onView={() => navigate(`/recruitment/candidates/${result.candidate.id}`)}
								onAddAnother={startAnother}
							/>
						)}

						{result.outcome === 'linked' && (
							<LinkedPanel
								result={result}
								jobLabel={jobLabel}
								file={file}
								candidateId={resolvedCandidateId}
								onView={() => resolvedCandidateId && navigate(`/recruitment/candidates/${resolvedCandidateId}`)}
								onOpenApplication={id => navigate(`/recruitment/applications/${id}`)}
								onAddAnother={startAnother}
							/>
						)}

						{result.outcome === 'ambiguous' && (
							<AmbiguousPanel
								result={result}
								jobLabel={jobLabel}
								pickingId={pickingId}
								error={ambiguousError}
								onPick={handlePick}
								onNoneOfThese={() => {
									resetToForm();
									setFormError('None of these selected — correct the phone/email so they identify a single person, then submit again.');
								}}
								onOpenCandidate={id => navigate(`/recruitment/candidates/${id}`)}
							/>
						)}
					</div>
				) : (
					<div className='mt-6 space-y-6'>
						{/* Resume first */}
						<Card className='space-y-3'>
							<h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>Resume</h2>
							<ResumeDropzone file={file} onFile={setFile} />
							<p className='text-xs text-gray-400'>Optional now — we will attach it right after the candidate is created.</p>
						</Card>

						{/* Details */}
						<Card className='space-y-4'>
							<h2 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>Candidate details</h2>
							<FloatingInput
								label='Full name'
								required
								value={fullName}
								onChange={setFullName}
								error={touched && !nameValid}
								errorMessage='Full name is required.'
							/>
							<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
								<FloatingInput
									label='Phone'
									required
									value={phone}
									onChange={setPhone}
									error={touched && (phoneError || contactMissing)}
									errorMessage={phoneError ? 'Enter a valid 10-digit mobile number.' : 'Phone or email is required.'}
								/>
								<FloatingInput
									label='Email'
									type='email'
									value={email}
									onChange={setEmail}
									error={touched && (emailError || contactMissing)}
									errorMessage={emailError ? 'Enter a valid email address.' : 'Phone or email is required.'}
								/>
							</div>
							<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
								<FloatingDropdown
									label='Job Opening'
									required
									options={jobOptions ?? []}
									value={jobId}
									onChange={setJobId}
									loading={jobsLoading}
									error={touched && !jobValid}
									errorMessage='Select a job opening.'
								/>
								<FloatingDropdown
									label='Source'
									required
									options={SOURCE_OPTIONS}
									value={source}
									onChange={setSource}
									searchable={false}
									error={touched && !sourceValid}
									errorMessage='Source is required.'
								/>
							</div>
							<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
								<FloatingInput label='Location' value={location} onChange={setLocation} />
								<FloatingInput label='Total experience (years)' type='number' value={experience} onChange={setExperience} />
								<FloatingInput label='Current company' value={currentCompany} onChange={setCurrentCompany} />
								<FloatingInput label='Current role' value={currentRole} onChange={setCurrentRole} />
								<FloatingInput label='Notice period (days)' type='number' value={noticePeriod} onChange={setNoticePeriod} />
							</div>

							<p className='rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-400'>
								Demo: use <span className='font-mono text-gray-500'>98200 00121</span> for an existing candidate,{' '}
								<span className='font-mono text-gray-500'>99301 00042</span> for an ambiguous match.
							</p>
						</Card>

						{formError && <InlineError message={formError} />}

						<div className='flex justify-end gap-2'>
							<Button variant='outline' onClick={() => navigate(-1)} disabled={intake.isPending}>
								Cancel
							</Button>
							<Button variant='primary' onClick={handleSubmit} loading={intake.isPending} disabled={!canSubmit}>
								Check &amp; create application
							</Button>
						</div>
					</div>
				)}
			</div>

			<Snackbar
				open={snackbar.open}
				message={snackbar.message}
				type={snackbar.type}
				onClose={() => setSnackbar(s => ({ ...s, open: false }))}
			/>
		</div>
	);
};

// ── result panels ─────────────────────────────────────────────────────────────

const CreatedPanel: React.FC<{
	candidate: Candidate;
	jobLabel: string;
	file: File | null;
	onView: () => void;
	onAddAnother: () => void;
}> = ({ candidate, jobLabel, file, onView, onAddAnother }) => (
	<div className='space-y-4'>
		<div className='flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-5'>
			<CheckCircle2 className='mt-0.5 h-6 w-6 shrink-0 text-green-600' />
			<div>
				<h2 className='text-lg font-semibold text-green-900'>Candidate added</h2>
				<p className='mt-0.5 text-sm text-green-800'>
					<span className='font-medium'>{candidate.full_name}</span> was created and applied to{' '}
					<span className='font-medium'>{jobLabel}</span>.
				</p>
			</div>
		</div>

		<Card>
			<h3 className='mb-3 text-sm font-semibold text-gray-700'>Attach a resume</h3>
			<ResumeUploadCard candidateId={candidate.id} initialFile={file} autoUpload={!!file} />
		</Card>

		<div className='flex justify-end gap-2'>
			<Button variant='outline' onClick={onAddAnother}>
				Add another
			</Button>
			<Button variant='primary' rightIcon={<ArrowRight className='h-4 w-4' />} onClick={onView}>
				View candidate
			</Button>
		</div>
	</div>
);

const LinkedPanel: React.FC<{
	result: IntakeLinked;
	jobLabel: string;
	file: File | null;
	candidateId: number | null;
	onView: () => void;
	onOpenApplication: (id: number) => void;
	onAddAnother: () => void;
}> = ({ result, jobLabel, file, candidateId, onView, onOpenApplication, onAddAnother }) => (
	<div className='space-y-4'>
		<div className='flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-5'>
			<UserCheck className='mt-0.5 h-6 w-6 shrink-0 text-green-600' />
			<div>
				<h2 className='text-lg font-semibold text-green-900'>We&rsquo;ve seen this person before</h2>
				<p className='mt-0.5 text-sm text-green-800'>
					A new application for <span className='font-medium'>{jobLabel}</span> was added to this person — no duplicate created.
				</p>
			</div>
		</div>

		<CandidateSummaryCard candidate={result.candidate} />

		{result.priorApplications && result.priorApplications.length > 0 && (
			<Card>
				<h3 className='mb-3 text-sm font-semibold text-gray-700'>Their prior applications</h3>
				<ul className='divide-y divide-gray-100'>
					{result.priorApplications.map((a: ApplicationRow) => (
						<li key={a.id}>
							<button
								type='button'
								onClick={() => onOpenApplication(a.id)}
								className='flex w-full items-center justify-between py-2.5 text-left hover:bg-gray-50'
							>
								<div className='min-w-0'>
									<p className='truncate text-sm font-medium text-gray-900'>{a.job_title}</p>
									<p className='text-xs text-gray-500'>
										<SourcePill source={a.source} /> · Applied {formatDate(a.applied_date)}
									</p>
								</div>
								<StageBadge stage={a.stage} />
							</button>
						</li>
					))}
				</ul>
			</Card>
		)}

		{candidateId && (
			<Card>
				<h3 className='mb-3 text-sm font-semibold text-gray-700'>Attach a resume</h3>
				<ResumeUploadCard candidateId={candidateId} initialFile={file} autoUpload={!!file} />
			</Card>
		)}

		<div className='flex justify-end gap-2'>
			<Button variant='outline' onClick={onAddAnother}>
				Add another
			</Button>
			<Button variant='primary' rightIcon={<ArrowRight className='h-4 w-4' />} onClick={onView}>
				View candidate
			</Button>
		</div>
	</div>
);

const AmbiguousPanel: React.FC<{
	result: IntakeAmbiguous;
	jobLabel: string;
	pickingId: number | null;
	error: string | null;
	onPick: (candidate: Candidate) => void;
	onNoneOfThese: () => void;
	onOpenCandidate: (id: number) => void;
}> = ({ result, jobLabel, pickingId, error, onPick, onNoneOfThese, onOpenCandidate }) => (
	<div className='space-y-4'>
		<div className='flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5'>
			<Users className='mt-0.5 h-6 w-6 shrink-0 text-amber-600' />
			<div>
				<h2 className='text-lg font-semibold text-amber-900'>This might be one of two people</h2>
				<p className='mt-0.5 text-sm text-amber-800'>
					The phone matched one person and the email another. Pick the right person to add the application for{' '}
					<span className='font-medium'>{jobLabel}</span> — we never merge automatically.
				</p>
			</div>
		</div>

		{error && <InlineError message={error} />}

		<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
			{result.candidates.map(c => (
				<CandidateSummaryCard
					key={c.id}
					candidate={c}
					footer={
						<div className='flex items-center justify-between gap-2'>
							<Button variant='link' size='sm' onClick={() => onOpenCandidate(c.id)}>
								View profile
							</Button>
							<Button
								variant='primary'
								size='sm'
								loading={pickingId === c.id}
								disabled={pickingId !== null}
								onClick={() => onPick(c)}
							>
								This is the person
							</Button>
						</div>
					}
				/>
			))}
		</div>

		<div className='flex justify-center'>
			<Button variant='outline' onClick={onNoneOfThese} disabled={pickingId !== null}>
				None of these — create new
			</Button>
		</div>
	</div>
);
